// 📄 tareaService.ts
// Tareas asignables dentro de un proyecto — a diferencia de "Mi Espacio >
// Tareas" (privado, ver miEspacioService.ts), esto es compartido y
// asignable a CUALQUIER miembro del proyecto, incluido el rol "cliente"
// (decisión explícita: un cliente puede tener una dependencia real, ej.
// "dar accesos al sistema", que bloquea al equipo si no se cumple). Mismo
// patrón general que incidenteService.ts (bitácora embebida como array,
// notificación por correo al asignar), pero sin los campos técnicos de
// incidente (ambiente/plataforma/SR) que acá no aplican.
import {
  doc,
  collection,
  collectionGroup,
  setDoc,
  updateDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  arrayUnion,
  serverTimestamp,
  Timestamp,
  Unsubscribe
} from "firebase/firestore";
import { db } from '../firebase';
import { ProjectMember } from './projectService.ts';
import { queueEmail } from './mailService.ts';
import { iniciarHitoSiPendiente } from './hitoService.ts';

export type TareaEstado = 'Pendiente' | 'En progreso' | 'Completada';
export const ESTADOS_TAREA: TareaEstado[] = ['Pendiente', 'En progreso', 'Completada'];

export interface ComentarioTarea {
  autor: string; // email
  autorUid: string;
  fecha: any;
  texto: string;
}

export interface TareaFormData {
  titulo: string;
  descripcion: string;
  asignadoA: string; // uid, obligatorio (a diferencia de incidentes, acá siempre hay un responsable)
  fechaLimite: string; // YYYY-MM-DD
  seccionRelacionada: string; // texto libre opcional, ej. "Configuraciones Generales"
  // Hito al que pertenece (opcional, ver hitoService.ts). "" = sin hito.
  hitoId?: string;
}

export interface Tarea extends TareaFormData {
  id: string;
  estado: TareaEstado;
  asignadoANombre: string; // email denormalizado (igual criterio que participantesInfo del chat: evita depender de un read que no todos pueden hacer)
  // Denormalizado a propósito (igual criterio que clienteNombre en
  // projects/{id}): la campanita de tareas del navbar (ver
  // useTareasPendientesGlobal.ts) consulta con collectionGroup a través de
  // TODOS los proyectos del usuario — sin esto, mostrar "de qué proyecto es
  // esta tarea" en esa lista necesitaría un read extra por proyecto.
  proyectoNombre: string;
  // Nombre del hito denormalizado, para mostrarlo sin leer los hitos.
  hitoNombre?: string;
  creadoPor: string;
  creadoPorNombre: string;
  creadoEn: any;
  comentarios: ComentarioTarea[];
}

// Solo la usa la campanita cross-proyecto — un doc de `Tarea` traído por
// collectionGroup, con el projectId inferido de su propio path (ver
// subscribeMisTareasGlobal).
export interface TareaConProyecto extends Tarea {
  projectId: string;
}

const tareasRef = (projectId: string) => collection(db, "projects", projectId, "tareas");
const tareaRef = (projectId: string, tareaId: string) => doc(db, "projects", projectId, "tareas", tareaId);

const escapeHtml = (str: string): string =>
  str.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>)[c]);

/**
 * Notifica por correo a quien quedó asignado — al crear la tarea, y de
 * nuevo si se reasigna a otra persona (ver updateTareaDatos). No corta la
 * operación si el envío falla, solo lo deja loggeado — mismo criterio que
 * notifyNuevoIncidente en incidenteService.ts.
 */
const notifyTareaAsignada = async (proyectoNombre: string, data: TareaFormData, asignadoEmail: string): Promise<void> => {
  await queueEmail({
    to: [asignadoEmail],
    subject: `[Tarea] ${data.titulo}`,
    html: `
      <p>Se te asignó una tarea en el proyecto <strong>${escapeHtml(proyectoNombre)}</strong>.</p>
      <p><strong>Título:</strong> ${escapeHtml(data.titulo)}</p>
      <p><strong>Fecha límite:</strong> ${data.fechaLimite}</p>
      ${data.seccionRelacionada ? `<p><strong>Sección relacionada:</strong> ${escapeHtml(data.seccionRelacionada)}</p>` : ''}
      ${data.descripcion ? `<p><strong>Descripción:</strong></p><p style="white-space:pre-wrap">${escapeHtml(data.descripcion)}</p>` : ''}
    `
  });
};

export const getTareas = async (projectId: string): Promise<Tarea[]> => {
  const snap = await getDocs(tareasRef(projectId));
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<Tarea, "id">) }))
    .sort((a, b) => (a.fechaLimite || '').localeCompare(b.fechaLimite || ''));
};

export const createTarea = async (
  projectId: string, proyectoNombre: string, data: TareaFormData, miembros: ProjectMember[], creadoPor: string, creadoPorNombre: string, hitoNombre = ''
): Promise<string> => {
  if (!data.titulo.trim()) throw new Error("El título es obligatorio.");
  if (!data.asignadoA) throw new Error("Elegí a quién se le asigna la tarea.");
  if (!data.fechaLimite) throw new Error("La fecha límite es obligatoria.");

  const asignado = miembros.find((m) => m.userId === data.asignadoA);
  if (!asignado) throw new Error("La persona asignada ya no es miembro de este proyecto.");

  const ref = doc(tareasRef(projectId));
  await setDoc(ref, {
    titulo: data.titulo.trim(),
    descripcion: data.descripcion.trim(),
    asignadoA: data.asignadoA,
    asignadoANombre: asignado.email,
    proyectoNombre,
    fechaLimite: data.fechaLimite,
    seccionRelacionada: data.seccionRelacionada.trim(),
    hitoId: data.hitoId || '',
    hitoNombre: data.hitoId ? hitoNombre : '',
    estado: 'Pendiente' as TareaEstado,
    creadoPor,
    creadoPorNombre,
    creadoEn: serverTimestamp(),
    comentarios: []
  });

  await notifyTareaAsignada(proyectoNombre, data, asignado.email).catch((err) => console.error('Error notificando la tarea asignada:', err));

  return ref.id;
};

export const updateTareaEstado = async (projectId: string, tareaId: string, estado: TareaEstado, hitoId = ''): Promise<void> => {
  await updateDoc(tareaRef(projectId, tareaId), { estado });
  // Una tarea que arranca o se completa pone su hito En curso (ver hitoService.ts).
  if (hitoId && estado !== 'Pendiente') await iniciarHitoSiPendiente(projectId, hitoId);
};

/**
 * `asignadoAAnterior` es necesario para saber si hay que notificar de
 * nuevo: si la tarea se reasigna a alguien distinto, esa persona nueva
 * también se entera por correo (mismo criterio que al crear); si no
 * cambió el asignado, no se manda nada de nuevo.
 */
export const updateTareaDatos = async (
  projectId: string, proyectoNombre: string, tareaId: string, data: TareaFormData, miembros: ProjectMember[], asignadoAAnterior: string, hitoNombre = ''
): Promise<void> => {
  if (!data.titulo.trim()) throw new Error("El título es obligatorio.");
  const asignado = miembros.find((m) => m.userId === data.asignadoA);
  if (!asignado) throw new Error("La persona asignada ya no es miembro de este proyecto.");

  await updateDoc(tareaRef(projectId, tareaId), {
    titulo: data.titulo.trim(),
    descripcion: data.descripcion.trim(),
    asignadoA: data.asignadoA,
    asignadoANombre: asignado.email,
    fechaLimite: data.fechaLimite,
    seccionRelacionada: data.seccionRelacionada.trim(),
    hitoId: data.hitoId || '',
    hitoNombre: data.hitoId ? hitoNombre : ''
  });

  if (data.asignadoA !== asignadoAAnterior) {
    await notifyTareaAsignada(proyectoNombre, data, asignado.email).catch((err) => console.error('Error notificando la reasignación:', err));
  }
};

export const addComentarioTarea = async (projectId: string, tareaId: string, autor: string, autorUid: string, texto: string): Promise<void> => {
  const limpio = texto.trim();
  if (!limpio) return;
  const entry: ComentarioTarea = { autor, autorUid, fecha: Timestamp.now(), texto: limpio };
  await updateDoc(tareaRef(projectId, tareaId), { comentarios: arrayUnion(entry) });
};

// Fechas LOCALES (no UTC): de noche en América, toISOString ya da el día
// siguiente y una tarea/ausencia se veía vencida o empezada un día antes.
const hoyStr = (): string => new Date().toLocaleDateString('en-CA');
const sumarDias = (fechaStr: string, dias: number): string => {
  const d = new Date(`${fechaStr}T00:00:00`);
  d.setDate(d.getDate() + dias);
  return d.toLocaleDateString('en-CA');
};

export const estaVencida = (tarea: Tarea): boolean =>
  tarea.estado !== 'Completada' && tarea.fechaLimite < hoyStr();

export const venceProximo = (tarea: Tarea, dias = 3): boolean =>
  tarea.estado !== 'Completada' && tarea.fechaLimite >= hoyStr() && tarea.fechaLimite <= sumarDias(hoyStr(), dias);

// Para el banner "tareas por vencer esta semana" (asignadas a MÍ, ver
// TareasProximasVencerBanner.tsx) — mismo umbral de 7 días que
// vacacionesService.seSuperponeConProximosDias, por consistencia.
export const venceEnProximaSemana = (tarea: Tarea): boolean =>
  tarea.estado !== 'Completada' && tarea.fechaLimite >= hoyStr() && tarea.fechaLimite <= sumarDias(hoyStr(), 7);

// Campanita del navbar: TODAS mis tareas, en TODOS mis proyectos, en
// tiempo real — collectionGroup en vez de N subscripciones por proyecto
// (más simple, un solo listener). Necesita una regla de Firestore aparte
// para el `list` de collectionGroup (ver firestore.rules,
// {path=**}/tareas/{tareaId} — un match anidado normal NO le aplica a
// collectionGroup, ya confirmado con el emulador en la sub-fase M1+M2).
export const subscribeMisTareasGlobal = (uid: string, callback: (tareas: TareaConProyecto[]) => void): Unsubscribe => {
  const q = query(collectionGroup(db, 'tareas'), where('asignadoA', '==', uid));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({
      id: d.id,
      projectId: d.ref.parent.parent!.id,
      ...(d.data() as Omit<Tarea, 'id'>)
    })));
  });
};
